import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from '@/App';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const contactInfo = [
    {
      icon: Phone,
      title: 'Téléphone',
      details: ['+242 05 087 12 50', '+242 06 476 45 82'],
      color: 'text-red-600'
    },
    {
      icon: Mail,
      title: 'Email',
      details: ['contact@colinoki.com', 'support@colinoki.com'],
      color: 'text-cyan-600'
    },
    {
      icon: MapPin,
      title: 'Adresse',
      details: ['Brazzaville, Congo', 'Pointe-Noire, Congo', 'Kinshasa, RDC'],
      color: 'text-green-600'
    },
    {
      icon: Clock,
      title: 'Horaires',
      details: ['Lun - Ven : 8h00 - 18h00', 'Sam : 9h00 - 13h00'],
      color: 'text-amber-600'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate sending message
    setTimeout(() => {
      toast.success('Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      setLoading(false);
    }, 1500);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-red-50 to-cyan-50 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-6">
                Contactez-nous
              </h1>
              <p className="text-lg text-gray-700">
                Une question ? Une suggestion ? Notre équipe est à votre écoute
              </p>
            </div>
          </div>
        </section>

        {/* Contact Info Cards */}
        <section className="py-12 -mt-8">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <Card key={index} className="hover:shadow-lg transition-shadow">
                      <CardContent className="pt-6 text-center">
                        <Icon className={`w-12 h-12 ${info.color} mx-auto mb-4`} />
                        <h3 className="font-semibold text-gray-900 mb-3">
                          {info.title}
                        </h3>
                        {info.details.map((detail, idx) => (
                          <p key={idx} className="text-sm text-gray-600 mb-1">
                            {detail}
                          </p>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-12">
                {/* Form */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Envoyez-nous un message
                  </h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name">Nom complet *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        placeholder="Votre nom"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        placeholder="votre@email.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="+242..."
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject">Sujet *</Label>
                      <Input
                        id="subject"
                        value={formData.subject}
                        onChange={(e) => setFormData({...formData, subject: e.target.value})}
                        required
                        placeholder="Objet de votre message"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        required
                        rows={6}
                        placeholder="Votre message..."
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gradient-to-r from-red-500 to-cyan-500 hover:from-red-600 hover:to-cyan-600"
                    >
                      {loading ? (
                        'Envoi en cours...'
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Envoyer le message
                        </>
                      )}
                    </Button>
                  </form>
                </div>

                {/* Info Side */}
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">
                    Pourquoi nous contacter ?
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        Support Technique
                      </h3>
                      <p className="text-gray-600">
                        Problème avec votre compte ou une livraison ? Notre équipe technique est là pour vous aider.
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        Partenariats
                      </h3>
                      <p className="text-gray-600">
                        Vous souhaitez devenir partenaire ou affilié ? Contactez-nous pour discuter des opportunités.
                      </p>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">
                        Suggestions
                      </h3>
                      <p className="text-gray-600">
                        Vos retours nous aident à améliorer nos services. N'hésitez pas à partager vos idées.
                      </p>
                    </div>
                    <div className="bg-gradient-to-br from-red-50 to-cyan-50 rounded-lg p-6">
                      <p className="font-semibold text-gray-900 mb-2">
                        Temps de réponse moyen
                      </p>
                      <p className="text-2xl font-bold gradient-text">
                        Moins de 24h
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <FooterNew />
    </div>
  );
};

export default ContactPage;